// @ts-nocheck
import { createConfig, createComponents } from '@gluestack-style/react';
import { themeConfig } from '../config/theme';
import { configObject as baseGluestackConfig, componentsTheme } from './gluestack-ui.config';
import { MyNavigationTheme } from './navigationTheme';
import type { TenantPublicTheme } from '../api/models/tenantPublicTheme';
import {
  darkenColor,
  desaturateColor,
  getContrastRatio,
  getLuminance,
  hexToRgb,
  hslToRgb,
  lightenColor,
  rgbToHex,
  rgbToHsl,
} from './colorMath';

/**
 * Hardcoded hues for "legacy" module colors (lime, teal, orange, etc.)
 */
const HARDCODED_MODULE_HUES = [
  75, // lime
  174, // teal
  24, // orange
  0, // red
  48, // yellow
  270, // purple
  188, // cyan
  300, // fuchsia
  258, // violet
  217, // blue
  150, // emerald
];

/**
 * Hardcoded names for "legacy" module colors (matching master branch)
 */
const LEGACY_MODULE_COLOR_NAMES = [
  'lime',
  'teal',
  'orange',
  'red',
  'yellow',
  'purple',
  'cyan',
  'fuchsia',
  'violet',
  'blue',
  'emerald',
];

/**
 * Hardcoded hues for "legacy" dimension colors
 */
const HARDCODED_DIMENSION_HUES = [
  190, // lightBlue
  170, // teal
  45, // amber
  0, // red
  280, // fuchsia
  200, // cyan
  120, // green
  60, // yellow
  30, // orange
  330, // pink
];

/**
 * Harmonize a status color with the brand color if they are in the same hue range
 */
const harmonize = (statusHex: string, brandHex: string) => {
  const rgbBrand = hexToRgb(brandHex);
  const rgbStatus = hexToRgb(statusHex);
  if (!rgbBrand || !rgbStatus) return statusHex;

  const hslBrand = rgbToHsl(rgbBrand.r, rgbBrand.g, rgbBrand.b);
  const hslStatus = rgbToHsl(rgbStatus.r, rgbStatus.g, rgbStatus.b);

  const hueDiff = Math.abs(hslBrand.h - hslStatus.h);
  const isRelated = hueDiff < 60 || hueDiff > 300;

  if (isRelated) {
    // Blend the hue completely, but also blend the saturation to match the brand's intensity
    // This avoids "neon" status colors clashing with a muted brand
    const harmonizedS = hslBrand.s * 0.4 + hslStatus.s * 0.6;
    const harmonizedRgb = hslToRgb(hslBrand.h, harmonizedS, hslStatus.l);
    return rgbToHex(harmonizedRgb.r, harmonizedRgb.g, harmonizedRgb.b);
  }

  return statusHex;
};

/**
 * Determine the best foreground color (tinted white or dark) for a given background
 * Returns the most saturated tinted version that meets contrast requirements,
 * otherwise falls back to pure white or neutral dark.
 */
const getOnColor = (bgHex: string, candidates: string[]): string => {
  const WHITE = '#FFFFFF';
  const DARK = '#344054'; // neutral700

  // 1. Try candidates from most saturated to least (e.g. 200 -> 100 -> 50)
  for (const candidate of candidates) {
    const ratio = getContrastRatio(bgHex, candidate);
    if (ratio >= 4.5) {
      return candidate;
    }
  }

  // 2. Fallback to pure white if candidates failed
  const whiteRatio = getContrastRatio(bgHex, WHITE);
  if (whiteRatio >= 4.5) {
    return WHITE;
  }

  // 3. Fallback to dark
  return DARK;
};

const DEFAULT_STATUS_COLORS = {
  success: '#10B981',
  error: '#EF4444',
  warning: '#F59E0B',
  info: '#3B82F6',
};

/**
 * Generates a perceptually uniform grading dimension color palette
 * Colors are evenly distributed on the HSL wheel, avoiding semantic conflict zones
 * and starting from a different offset than modules to minimize clashing.
 */
const generateDimensionColorPalette = (brandPrimaryHex: string, count = 10) => {
  const avoidRanges = [
    { h: 0, range: 25 },
    { h: 120, range: 25 },
  ];

  let startHue = 0;
  let hueStep = 0;

  if (themeConfig.useDynamicDimensionColors) {
    const brandRgb = hexToRgb(brandPrimaryHex);
    const brandHsl = brandRgb ? rgbToHsl(brandRgb.r, brandRgb.g, brandRgb.b) : { h: 0, s: 0, l: 0 };

    // Start from brand hue offset by 180° (complementary) to distinguish from modules
    startHue = (brandHsl.h + 180) % 360;
    hueStep = 360 / count;
  }

  const palette: Record<string, string> = {};

  for (let i = 0; i < count; i++) {
    let hue;

    if (themeConfig.useDynamicDimensionColors) {
      hue = (startHue + i * hueStep) % 360;

      for (const avoid of avoidRanges) {
        const distance = Math.abs(((hue - avoid.h + 180) % 360) - 180);
        if (distance < avoid.range) {
          hue = (avoid.h + avoid.range + 5) % 360;
        }
      }
    } else {
      hue = HARDCODED_DIMENSION_HUES[i] || 0;
    }

    const saturation = 65; // Slightly more saturated for dimensions than modules
    const shades = [
      { shade: '50', lightness: 95 },
      { shade: '100', lightness: 90 },
      { shade: '200', lightness: 80 },
      { shade: '300', lightness: 70 },
      { shade: '400', lightness: 60 },
      { shade: '500', lightness: 50 },
      { shade: '600', lightness: 40 }, // Darkened from 42 to 40 for better contrast on white
      { shade: '700', lightness: 32 }, // Darkened from 35 to 32 for better readability in sidebar
      { shade: '800', lightness: 22 }, // Darkened from 25 to 22
      { shade: '900', lightness: 12 }, // Darkened from 15 to 12
    ];

    for (const { shade, lightness } of shades) {
      const rgb = hslToRgb(hue, saturation, lightness);
      palette[`dim${i}${shade}`] = rgbToHex(rgb.r, rgb.g, rgb.b);
    }
  }

  return palette;
};

/**
 * Generates a perceptually uniform module color palette
 * Colors are evenly distributed on the HSL wheel, avoiding semantic conflict zones
 *
 * @param brandPrimaryHex - Brand color to potentially harmonize with
 * @param count - Number of module colors to generate (default: 10)
 * @returns Object with moduleN_shade tokens (e.g., module0_400, module0_500, module0_600)
 */
const generateModuleColorPalette = (brandPrimaryHex: string, count = 10) => {
  // If we're not using dynamic colors, we don't generate them here.
  // Instead, we rely on the default Gluestack colors for the legacy names.
  if (!themeConfig.useDynamicModuleColors) {
    return {};
  }

  // Hue ranges to avoid (semantic conflicts)
  const avoidRanges = [
    { h: 0, range: 25 }, // Avoid red zone (error semantic)
    { h: 120, range: 25 }, // Avoid pure green zone (success semantic)
  ];

  let startHue = 0;
  let hueStep = 0;

  if (themeConfig.useDynamicModuleColors) {
    // Get brand hue as starting point for harmony
    const brandRgb = hexToRgb(brandPrimaryHex);
    const brandHsl = brandRgb ? rgbToHsl(brandRgb.r, brandRgb.g, brandRgb.b) : { h: 0, s: 0, l: 0 };

    // Start from brand hue offset by 30° to avoid direct overlap
    startHue = (brandHsl.h + 30) % 360;
    hueStep = 360 / count;
  }

  const palette: Record<string, string> = {};

  for (let i = 0; i < count; i++) {
    let hue;

    if (themeConfig.useDynamicModuleColors) {
      hue = (startHue + i * hueStep) % 360;

      // Check if hue falls in avoided range and shift if needed
      for (const avoid of avoidRanges) {
        const distance = Math.abs(((hue - avoid.h + 180) % 360) - 180);
        if (distance < avoid.range) {
          // Shift hue past the avoided range
          hue = (avoid.h + avoid.range + 5) % 360;
        }
      }
    } else {
      hue = HARDCODED_MODULE_HUES[i] || 0;
    }

    // Generate shades with consistent saturation and varying lightness
    // Using saturation 60-65% for vibrant but not overwhelming colors
    const saturation = 62;

    // Generate the shades we actually use: 50 (subtle bg), 400, 500, 600, 700
    const shades = [
      { shade: '50', lightness: 95 }, // Very light (for subtle backgrounds)
      { shade: '100', lightness: 90 },
      { shade: '200', lightness: 80 },
      { shade: '300', lightness: 70 },
      { shade: '400', lightness: 60 }, // Used for borders, accents
      { shade: '500', lightness: 50 }, // Primary shade
      { shade: '600', lightness: 42 }, // Hover state
      { shade: '700', lightness: 35 }, // Dark text on light bg
      { shade: '800', lightness: 25 },
      { shade: '900', lightness: 15 },
    ];

    for (const { shade, lightness } of shades) {
      const rgb = hslToRgb(hue, saturation, lightness);
      palette[`module${i}${shade}`] = rgbToHex(rgb.r, rgb.g, rgb.b);
    }
  }

  return palette;
};

/**
 * Returns the list of generated module color names for the palette selector
 */
const getModuleColorNames = (count = 10): string[] => {
  if (!themeConfig.useDynamicModuleColors) {
    return LEGACY_MODULE_COLOR_NAMES;
  }
  return Array.from({ length: count }, (_, i) => `module${i}`);
};

/**
 * Legacy dark/neutral brand palette
 */
const LEGACY_PRIMARY_PALETTE = {
  primary50: '#F9FAFB',
  primary100: '#F2F4F7',
  primary200: '#EAECF0',
  primary300: '#D0D5DD',
  primary400: '#98A2B3',
  primary500: '#667085',
  primary600: '#475467',
  primary700: '#344054',
  primary800: '#1D2939',
  primary900: '#101828',
};

/**
 * Generates a 50-900 palette from a base color
 */
const generatePalette = (hex: string, prefix: string, desaturateAmount = 0.2) => {
  const mutedBase = desaturateAmount > 0 ? desaturateColor(hex, desaturateAmount) : hex;

  return {
    [`${prefix}50`]: lightenColor(mutedBase, 0.95),
    [`${prefix}100`]: lightenColor(mutedBase, 0.9),
    [`${prefix}200`]: lightenColor(mutedBase, 0.8),
    [`${prefix}300`]: lightenColor(mutedBase, 0.6),
    [`${prefix}400`]: lightenColor(mutedBase, 0.3),
    [`${prefix}500`]: mutedBase,
    [`${prefix}600`]: darkenColor(mutedBase, 0.1),
    [`${prefix}700`]: darkenColor(mutedBase, 0.2),
    [`${prefix}800`]: darkenColor(mutedBase, 0.3),
    [`${prefix}900`]: darkenColor(mutedBase, 0.4),
  };
};

export type ThemeColors = ReturnType<typeof calculateThemeColors>;

/**
 * Calculate theme colors based on tenant settings
 */
export const calculateThemeColors = (theme: TenantPublicTheme | null) => {
  const defaultPrimary = '#A9D14C';
  const defaultSecondary = '#5FB5D9';
  const defaultAccent = '#4A4A4A';

  const primaryColor = themeConfig.useTenantBrandColors
    ? (theme?.primaryColor || defaultPrimary)
    : LEGACY_PRIMARY_PALETTE.primary500;
  const secondaryColor = themeConfig.useTenantBrandColors
    ? (theme?.secondaryColor || defaultSecondary)
    : LEGACY_PRIMARY_PALETTE.primary400; // Slightly lighter for secondary
  const accentColor = themeConfig.useTenantBrandColors
    ? (theme?.accentColor || defaultAccent)
    : LEGACY_PRIMARY_PALETTE.primary600; // Slightly darker for accent

  // Harmonize statuses
  const harmonizedSuccess = harmonize(DEFAULT_STATUS_COLORS.success, primaryColor);
  const harmonizedError = harmonize(DEFAULT_STATUS_COLORS.error, primaryColor);
  const harmonizedWarning = harmonize(DEFAULT_STATUS_COLORS.warning, primaryColor);
  const harmonizedInfo = harmonize(DEFAULT_STATUS_COLORS.info, primaryColor);

  // Generate palettes
  const primaryPalette = themeConfig.useTenantBrandColors
    ? generatePalette(primaryColor, 'primary')
    : LEGACY_PRIMARY_PALETTE;
  const secondaryPalette = themeConfig.useTenantBrandColors
    ? generatePalette(secondaryColor, 'secondary')
    : {
      secondary50: LEGACY_PRIMARY_PALETTE.primary50,
      secondary100: LEGACY_PRIMARY_PALETTE.primary100,
      secondary200: LEGACY_PRIMARY_PALETTE.primary200,
      secondary300: LEGACY_PRIMARY_PALETTE.primary300,
      secondary400: LEGACY_PRIMARY_PALETTE.primary400,
      secondary500: LEGACY_PRIMARY_PALETTE.primary500,
      secondary600: LEGACY_PRIMARY_PALETTE.primary600,
      secondary700: LEGACY_PRIMARY_PALETTE.primary700,
      secondary800: LEGACY_PRIMARY_PALETTE.primary800,
      secondary900: LEGACY_PRIMARY_PALETTE.primary900,
    };
  const accentPalette = themeConfig.useTenantBrandColors
    ? generatePalette(accentColor, 'accent')
    : {
      accent50: LEGACY_PRIMARY_PALETTE.primary50,
      accent100: LEGACY_PRIMARY_PALETTE.primary100,
      accent200: LEGACY_PRIMARY_PALETTE.primary200,
      accent300: LEGACY_PRIMARY_PALETTE.primary300,
      accent400: LEGACY_PRIMARY_PALETTE.primary400,
      accent500: LEGACY_PRIMARY_PALETTE.primary500,
      accent600: LEGACY_PRIMARY_PALETTE.primary600,
      accent700: LEGACY_PRIMARY_PALETTE.primary700,
      accent800: LEGACY_PRIMARY_PALETTE.primary800,
      accent900: LEGACY_PRIMARY_PALETTE.primary900,
    };
  const successPalette = generatePalette(harmonizedSuccess, 'success', 0);
  const errorPalette = generatePalette(harmonizedError, 'error', 0);
  const warningPalette = generatePalette(harmonizedWarning, 'warning', 0);
  const infoPalette = generatePalette(harmonizedInfo, 'info', 0);

  // Generate module and dimension color palettes
  const moduleColors = generateModuleColorPalette(
    primaryColor,
    themeConfig.useDynamicModuleColors ? 10 : HARDCODED_MODULE_HUES.length,
  );
  const dimensionColors = generateDimensionColorPalette(
    primaryColor,
    themeConfig.useDynamicDimensionColors ? 10 : HARDCODED_DIMENSION_HUES.length,
  );

  return {
    primaryColor,
    secondaryColor,
    accentColor,
    ...primaryPalette,
    ...secondaryPalette,
    ...accentPalette,
    ...successPalette,
    ...errorPalette,
    ...warningPalette,
    ...infoPalette,
    ...moduleColors,
    ...dimensionColors,

    // Semantic Status Aliases (Mapping the pearls to the tuned palette)
    statusSuccess: successPalette.success600,
    statusSuccessSubtle: successPalette.success50,
    statusError: errorPalette.error600,
    statusErrorSubtle: errorPalette.error50,
    statusWarning: warningPalette.warning600,
    statusWarningSubtle: warningPalette.warning50,
    statusInfo: infoPalette.info600,
    statusInfoSubtle: infoPalette.info50,

    // For DARK/SOLID surfaces: Use computed contrast (tinted white vs neutral dark)
    // Try multiple shades from the palette to get a nice tinted "on-color"
    // that still meets WCAG AA compliance (4.5:1 contrast)
    // We try darker/more saturated shades first (400, 300...)
    // textOnBrand is for HEADINGS on brand backgrounds (needs highest contrast/brightness)
    textOnBrand: getOnColor(primaryPalette.primary700, [
      primaryPalette.primary100,
      primaryPalette.primary50,
      primaryPalette.primary200,
    ]),
    // textOnBrandMuted is for secondary text on brand backgrounds (more saturated tint)
    textOnBrandMuted: getOnColor(primaryPalette.primary700, [
      primaryPalette.primary300,
      primaryPalette.primary400,
      primaryPalette.primary200,
    ]),
    textOnSecondary: getOnColor(secondaryPalette.secondary700, [
      secondaryPalette.secondary200,
      secondaryPalette.secondary100,
      secondaryPalette.secondary50,
    ]),
    textOnSuccess: getOnColor(successPalette.success600, [
      successPalette.success200,
      successPalette.success100,
      successPalette.success50,
    ]),
    textOnError: getOnColor(errorPalette.error600, [
      errorPalette.error200,
      errorPalette.error100,
      errorPalette.error50,
    ]),
    textOnWarning: getOnColor(warningPalette.warning600, [
      warningPalette.warning200,
      warningPalette.warning100,
      warningPalette.warning50,
    ]),
    textOnInfo: getOnColor(infoPalette.info600, [infoPalette.info200, infoPalette.info100, infoPalette.info50]),
    textOnDark: '#F2F4F7', // neutral100 - for dark gray backgrounds

    // For SUBTLE/LIGHT surfaces: Use the dark end of the SAME palette
    // This maintains brand cohesion (e.g., dark green text on light green bg)
    textOnBrandSubtle: primaryPalette.primary800,
    textOnSecondarySubtle: secondaryPalette.secondary800,
    textOnSuccessSubtle: successPalette.success700,
    textOnErrorSubtle: errorPalette.error700,
    textOnWarningSubtle: warningPalette.warning800,
    textOnInfoSubtle: infoPalette.info700,

    // Semantic surface tokens (explicit hex for debugging)
    bgBrand: primaryPalette.primary700,
    bgBrandSubtle: primaryPalette.primary50,
    bgSecondary: secondaryPalette.secondary50,

    // Semantic border tokens
    borderBrand: primaryPalette.primary500,
    borderFocus: primaryPalette.primary400,
    // Status outlines: the *Subtle tones are fills and vanish as 1px borders on white.
    borderError: errorPalette.error300,
    borderSuccess: successPalette.success300,

    // Semantic brand action tokens
    brandBase: primaryPalette.primary700,
    brandSubtle: primaryPalette.primary200,
    brandStrong: primaryPalette.primary700,
    brandHover: primaryPalette.primary600,
    brandActive: primaryPalette.primary700,

    // Semantic action tokens
    actionPrimary: primaryPalette.primary700,
    actionPrimaryHover: primaryPalette.primary600,
    actionSecondary: secondaryPalette.secondary500,
    actionSecondaryHover: secondaryPalette.secondary600,

    // Semantic typography brand token
    textBrand: primaryPalette.primary800,

    // Semantic typography aliases (explicit hex for stability)
    textHeading: '#101828', // neutral900
    textBody: '#344054', // neutral700
    textSubtle: '#667085', // neutral500
    textHint: '#98A2B3', // neutral400
    textDisabled: '#D0D5DD', // neutral300
    textInverted: '#FFFFFF',

    // Semantic border tokens (ensure consistency even when overriding)
    borderLight: '#EAECF0', // neutral200
    borderMedium: '#D0D5DD', // neutral300
    borderStrong: '#98A2B3', // neutral400

    // Semantic background tokens
    bgApp: '#FFFFFF',
    bgCard: '#FFFFFF',
    bgSubtle: '#F9FAFB', // neutral50
    bgHover: '#F2F4F7', // neutral100
    bgDark: '#1D2939', // neutral800
    bgOverlay: 'rgba(16, 24, 40, 0.05)',
  };
};

/**
 * =============================================================================
 * CRITICAL: "CLEAN ROOM" THEME GENERATION
 * =============================================================================
 *
 * Generates the full dynamic theme configuration with tenant-specific colors.
 *
 * WHY createComponents() IS CALLED HERE:
 * Gluestack-style v1 resolves component theme tokens (like $brandBase in Button.ts)
 * at createComponents() time, NOT at render time. This means:
 *
 * - If createComponents() is called at module load (before tenant data exists),
 *   tokens resolve to default values and are "baked in" permanently.
 * - To get tenant colors into component themes, we MUST call createComponents()
 *   AFTER we have the tenant's token values.
 *
 * THE FLOW:
 * 1. Tenant settings load -> setSettings() in TenantSettingsStore
 * 2. setSettings() calls this function with tenant theme
 * 3. We calculate dynamic token values (brandBase, bgBrand, etc.)
 * 4. We call createComponents(componentsTheme) - this resolves tokens NOW
 * 5. We create the final config with these freshly-resolved components
 * 6. ThemeWrapper mounts GluestackUIProvider with the correct theme
 *
 * DO NOT:
 * - Use a cached/pre-built componentsConfig here
 * - Skip calling createComponents()
 * - Return baseConfig directly without processing
 *
 * Related files:
 * - TenantSettingsStore.ts (calls this function, gluestackConfig starts null)
 * - App.jsx (ThemeWrapper waits for config before mounting)
 * - gluestack-ui.config.ts (exports componentsTheme for re-creation)
 * =============================================================================
 */
export const generateDynamicTheme = (theme: TenantPublicTheme | null) => {
  const themeColors = calculateThemeColors(theme);
  const moduleCount = themeConfig.useDynamicModuleColors ? 10 : HARDCODED_MODULE_HUES.length;
  const dimensionCount = themeConfig.useDynamicDimensionColors ? 10 : HARDCODED_DIMENSION_HUES.length;

  const moduleColorNames = getModuleColorNames(moduleCount);
  const dimensionColorNames = Array.from({ length: dimensionCount }, (_, i) => `dim${i}`);

  // Build the complete tokens object with dynamic overrides
  const dynamicTokens = {
    ...baseGluestackConfig.tokens,
    colors: {
      ...baseGluestackConfig.tokens.colors,
      ...themeColors,
    },
    moduleColorPalette: moduleColorNames,
    dimensionColorPalette: dimensionColorNames,
  };

  // CRITICAL: Create FRESH components with the dynamic token values.
  // This is where component theme tokens get resolved to tenant colors.
  // DO NOT use a cached componentsConfig - it will have default colors baked in!
  const freshComponents = createComponents(componentsTheme);

  const gluestackConfig = createConfig({
    ...baseGluestackConfig,
    tokens: dynamicTokens,
    components: freshComponents,
  });

  const navigationTheme = {
    ...MyNavigationTheme,
    colors: {
      ...MyNavigationTheme.colors,
      primary: (themeColors as any).primary700,
    },
  };

  return {
    themeColors,
    gluestackConfig,
    navigationTheme,
  };
};
